import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Input, Tooltip } from 'antd';
import dayjs from 'dayjs';
import AutoAddressInput from 'components/AutoAddressInput';
import { AllState } from 'constants/common';
import type { ServiceAddressItem, TAddressPrefill } from '../types';
import { serviceAddressComplete, serviceAddressError } from '../validators';
import s from '../common.module.less';

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

interface Props {
    // action needed 深链定位：初始选中的州 chip（在 states 内时生效）
    focusState?: string;
    states: string[];
    value: ServiceAddressItem[];
    onChange: (items: ServiceAddressItem[]) => void;
    onContinue: () => void;
    continueLabel?: string;
    // group 宿主（ProviderPayerModal）传 true：零侧边对齐标题、footer 分隔线通边
    flush?: boolean;
    onBack?: () => void;
    saving?: boolean;
    // Basic information 的 contactAddress，只预填与其同州的州块
    prefill?: TAddressPrefill;
    // review 行内编辑：只渲染表单 body，动作按钮由宿主（ReviewSection 吸底条）提供
    hideFooter?: boolean;
}

// Service address by state：每州一份 filing 地址 + practice start date，全部必填。
const ServiceAddressStep = ({
    states,
    value,
    onChange,
    onContinue,
    onBack,
    saving,
    prefill,
    continueLabel = 'Continue',
    flush,
    hideFooter,
    focusState,
}: Props) => {
    const [activeState, setActiveState] = useState(focusState && states.includes(focusState) ? focusState : states[0]);
    const effectiveState = states.includes(activeState) ? activeState : states[0];

    // 首次进入时把同州的 contactAddress 预填成一条真实数据（street/city/zip；start date 无预填源）
    useEffect(() => {
        if (!prefill?.state || !states.includes(prefill.state)) return;
        if (value.some((a) => a.state === prefill.state)) return;
        onChange([
            ...value,
            {
                state: prefill.state,
                addressLine1: prefill.street || '',
                city: prefill.city || '',
                zip: prefill.zip || '',
            },
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const itemFor = (st: string) => value.find((a) => a.state === st);

    // 地址联想选中时 street/city/zip 三个回调同 tick 连续触发，基于 props 计算会互相覆盖，
    // 用 ref 持有最新数组让同 tick 的多次 patch 能够叠加
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    const patch = (st: string, p: Partial<ServiceAddressItem>) => {
        const base = valueRef.current;
        const idx = base.findIndex((a) => a.state === st);
        let next: ServiceAddressItem[];
        if (idx === -1) next = [...base, { state: st, ...p }];
        else {
            next = [...base];
            next[idx] = { ...next[idx], ...p };
        }
        valueRef.current = next;
        onChange(next);
    };

    const complete = serviceAddressComplete(states, value);

    const renderBlock = (st: string) => {
        const item = itemFor(st);
        const err = serviceAddressError(item);
        return (
            <div key={st}>
                <div className={s.svcBlock}>
                    <div>
                        <span className={s.subFieldLabel}>Street address</span>
                        <AutoAddressInput
                            size="large"
                            value={item?.addressLine1 || ''}
                            placeholder="e.g. 224234 Rose"
                            displayFields={['street']}
                            onChange={(v) => patch(st, { addressLine1: v })}
                            onCityChange={(v) => patch(st, { city: v })}
                            onPostcodeChange={(v) => patch(st, { zip: v })}
                        />
                    </div>
                    <div className={s.svcRow}>
                        <div>
                            <span className={s.subFieldLabel}>Suite / unit (optional)</span>
                            <Input
                                size="large"
                                value={item?.addressLine2 || ''}
                                placeholder="Ex. Unit"
                                onChange={(e) => patch(st, { addressLine2: e.target.value })}
                            />
                        </div>
                        <div>
                            <span className={s.subFieldLabel}>City</span>
                            <Input
                                size="large"
                                value={item?.city || ''}
                                placeholder="City"
                                onChange={(e) => patch(st, { city: e.target.value })}
                            />
                        </div>
                        <div>
                            <span className={s.subFieldLabel}>ZIP code</span>
                            <Input
                                size="large"
                                value={item?.zip || ''}
                                placeholder="5 digits"
                                onChange={(e) => patch(st, { zip: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <span className={`${s.subFieldLabel} ${s.labelWithTip}`}>
                            Practice start date at this address{' '}
                            <Tooltip title="Tell us when you started, or will start, seeing patients at this address. This is required even if you only offer virtual visits here.">
                                <svg
                                    viewBox="64 64 896 896"
                                    focusable="false"
                                    data-icon="question-circle"
                                    width="14"
                                    height="14"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    style={{ verticalAlign: 'middle', color: '#9ca3af', cursor: 'help' }}
                                >
                                    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
                                    <path d="M623.6 316.7C593.6 290.4 554 276 512 276s-81.6 14.5-111.6 40.7C369.2 344 352 380.7 352 420v7.6c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V420c0-44.1 43.1-80 96-80s96 35.9 96 80c0 31.1-22 59.6-56.1 72.7-21.2 8.1-39.2 22.3-52.1 40.9-13.1 19-19.9 41.8-19.9 64.9V620c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8v-22.7a48.3 48.3 0 0130.9-44.8c59-22.7 97.1-74.7 97.1-132.5.1-39.3-17.1-76-48.3-103.3zM472 732a40 40 0 1080 0 40 40 0 10-80 0z" />
                                </svg>
                            </Tooltip>
                        </span>
                        <DatePicker
                            size="large"
                            style={{ width: '100%' }}
                            value={item?.practiceStartDate ? dayjs(item.practiceStartDate) : null}
                            onChange={(d) => patch(st, { practiceStartDate: d ? d.format('YYYY-MM-DD') : '' })}
                        />
                    </div>
                </div>
                {err && <p className={s.svcError}>{err}</p>}
            </div>
        );
    };

    return (
        <>
            <div className={`${s.modalBody} ${flush ? s.flushBody : ''}`}>
                <div className={s.stateChips}>
                    {states.map((st) => (
                        <button
                            key={st}
                            type="button"
                            className={`${s.stateChip} ${st === effectiveState ? s.stateChipActive : ''}`}
                            onClick={() => setActiveState(st)}
                        >
                            {getStateName(st)}
                        </button>
                    ))}
                </div>
                {renderBlock(effectiveState)}
            </div>
            {!hideFooter && (
                <div className={`${s.modalFooter} ${flush ? s.flushFooter : ''}`}>
                    <Button
                        type="primary"
                        block
                        size="large"
                        className={s.primaryBtn}
                        disabled={!complete}
                        loading={saving}
                        onClick={onContinue}
                    >
                        {continueLabel}
                    </Button>
                    {onBack && (
                        <Button block size="large" className={s.backBtn} onClick={onBack}>
                            Back
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default ServiceAddressStep;
